import Enums from "@/enums/enums";
import { DecryptionIndexedDbCash, EncryptionIndexedDbCash } from "@/helper/shareHelper";
const indexedDB = window.indexedDB || window.mozIndexedDB || window.webkitIndexedDB || window.msIndexedDB;

const CacheDBName = "CacheDB";
const CacheDBVersion = 13; // must increase when modify any object stores
const ObjectStores = {
  AccessToken: "AccessToken",
  UserProfile: "UserProfile",
  SelectedMerchant: "SelectedMerchant",
  SettingTable: "SettingTable",
  SettingViewTransaction: "SettingViewTransaction",
  SettingPriceProduct: "SettingPriceProduct",
  Account: "Account",
  UserInApp: "UserInApp",
  Package: "Package",
  SettingQuotationPartial: "SettingQuotationPartial",
  SettingPurchaseOrderPartial: "SettingPurchaseOrderPartial",
  BankAccount: "BankAccount",
  Product: "Product",
  ContactStatementRemarkFrequency: "ContactStatementRemarkFrequency",
  AssetTemplate: "AssetTemplate",
};
const ObjectStoresDefaultCount = Object.keys(ObjectStores).length;

const GetCurrentCacheDBVersion = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(CacheDBName);

    request.onsuccess = (e) => {
      const db = e.target.result;
      const objectStoresCount = db.objectStoreNames.length;
      db.close();
      resolve(objectStoresCount !== ObjectStoresDefaultCount ? 0 : db.version);
    };

    request.onerror = (e) => {
      resolve(0);
    };
  });
};

const DeleteCacheIndexedDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(CacheDBName);

    request.onsuccess = (e) => {
      resolve(true);
    };

    request.onerror = (e) => {
      console.error("Failed to delete database CacheDB:", e.target.error);
      resolve(false);
    };

    request.onblocked = (e) => {
      console.warn("Deletion of database CacheDB is blocked, possibly because it's open in another tab or process.");
      resolve(false);
    };
  });
};

export const OpenCacheIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    if (!indexedDB) {
      reject(new Error("Your browser doesn't support IndexedBD"));
      return;
    }

    const currentCacheDBVersion = await GetCurrentCacheDBVersion();

    if (currentCacheDBVersion !== CacheDBVersion) {
      await DeleteCacheIndexedDB();
    }

    const request = indexedDB.open(CacheDBName, CacheDBVersion);

    request.onerror = (e) => {
      console.error(`Failed to open database CacheDB: ${e.target.error}`);
      reject(e.target.error);
    };

    request.onsuccess = (e) => {
      const db = e.target.result;
      resolve(db);
    };

    request.onupgradeneeded = (e) => {
      const db = e.target.result;

      for (const [key, value] of Object.entries(ObjectStores)) {
        if (!db.objectStoreNames.contains(value)) {
          db.createObjectStore(value, { keyPath: null, autoIncrement: false });
        }
      }
    };
  });
};

//#region AccessToken
export const AddAccessTokenToIndexedDB = (accessToken) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.AccessToken], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.AccessToken);
      const request = objectStore.get(1);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(accessToken);
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, 1);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddUserProfileToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, 1);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddAccessTokenToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };
      request.onerror = (err) => {
        console.error(`Error to request AddAccessTokenToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetAccessTokenFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db.transaction([ObjectStores.AccessToken]).objectStore(ObjectStores.AccessToken).getAll();

      request.onsuccess = () => {
        let accessTokenList = [];
        if (request.result) {
          for (let resultItem of request.result) {
            accessTokenList.push(DecryptionIndexedDbCash(resultItem));
          }
        }
        db.close();
        if (accessTokenList && accessTokenList.length > 0) {
          resolve(accessTokenList[0]);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to get user profile: ${err}`);
        resolve(null);
      };
    } else {
      resolve(null);
    }
  });
};

export const ClearAccessTokenFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.AccessToken], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.AccessToken)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
//#endregion

//#region UserProfile
export const AddUserProfileToIndexedDB = (userProfile) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.UserProfile], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.UserProfile);
      const request = objectStore.get(userProfile.userId);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(JSON.stringify(userProfile));
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, userProfile.userId);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddUserProfileToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, userProfile.userId);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddUserProfileToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };
      request.onerror = (err) => {
        console.error(`Error to request AddUserProfileToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetUserProfileFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db.transaction([ObjectStores.UserProfile]).objectStore(ObjectStores.UserProfile).getAll();

      request.onsuccess = () => {
        let userProfileList = [];
        if (request.result) {
          for (let resultItem of request.result) {
            userProfileList.push(JSON.parse(DecryptionIndexedDbCash(resultItem)));
          }
        }
        db.close();
        if (userProfileList && userProfileList.length > 0) {
          resolve(userProfileList[0]);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to get user profile: ${err}`);
        resolve(null);
      };
    } else {
      resolve(null);
    }
  });
};

export const ClearUserProfileFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.UserProfile], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.UserProfile)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
//#endregion

//#region SelectedMerchant
export const AddSelectedMerchantToIndexedDB = (selectedMerchant) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SelectedMerchant], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SelectedMerchant);
      const request = objectStore.get(selectedMerchant.merchantId);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(JSON.stringify(selectedMerchant));
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, selectedMerchant.merchantId);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddSelectedMerchantToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, selectedMerchant.merchantId);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddSelectedMerchantToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };
      request.onerror = (err) => {
        console.error(`Error to request AddSelectedMerchantToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetSelectedMerchantFromIndexedDB = (merchantId) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SelectedMerchant], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SelectedMerchant);
      const request = objectStore.get(merchantId);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          let merchant = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(merchant);
        } else {
          resolve(null);
        }
      };
      request.onerror = (err) => {
        console.error(`Error to request AddSelectedMerchantToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};
export const DeleteSelectedMerchantFromIndexedDB = (merchantId) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SelectedMerchant], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SelectedMerchant);
      const request = objectStore.delete(merchantId);
      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
export const ClearSelectedMerchantFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.SelectedMerchant], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SelectedMerchant)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
//#endregion

//#region SettingTable
export const AddSettingTableToIndexedDB = (key, settingTable) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingTable], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingTable);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(JSON.stringify(settingTable));
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, key);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddSettingTableToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, key);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddSettingTableToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request AddSettingTableToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetSettingTableFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingTable], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingTable);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          const settingTable = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(settingTable);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request GetSettingTableFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteSettingTableFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingTable], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingTable);
      const request = objectStore.delete(key);

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };

      request.onerror = (err) => {
        console.error(`Error to request DeleteSettingTableFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve();
    }
  });
};

export const ClearSettingTableFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.SettingTable], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingTable)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
//#endregion

//#region SettingViewTransaction
export const AddSettingViewTransactionToIndexedDB = (key, settingViewTransaction) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingViewTransaction], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingViewTransaction);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(JSON.stringify(settingViewTransaction));
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, key);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddSettingViewTransactionToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, key);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddSettingViewTransactionToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request AddSettingViewTransactionToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetSettingViewTransactionFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingViewTransaction], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingViewTransaction);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          const settingViewTransaction = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(settingViewTransaction);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request GetSettingViewTransactionFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteSettingViewTransactionFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingViewTransaction], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingViewTransaction);
      const request = objectStore.delete(key);

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };

      request.onerror = (err) => {
        console.error(`Error to request DeleteSettingViewTransactionFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve();
    }
  });
};

export const DeleteMultipleSettingViewTransactionFromIndexDB = (keyList) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingViewTransaction], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingViewTransaction);

      const deletePromises = keyList.map((key) => {
        return new Promise((resolveDelete, rejectDelete) => {
          let deleteRequest = objectStore.delete(key);

          deleteRequest.onsuccess = function () {
            resolveDelete();
          };

          deleteRequest.onerror = function () {
            resolveDelete();
          };
        });
      });

      Promise.all(deletePromises)
        .then(() => {
          db.close();
          resolve();
        })
        .catch((err) => {
          console.error(`Error to request DeleteMultipleSettingViewTransactionFromIndexDB: ${err}`);
          db.close();
          resolve(0);
        });
    } else {
      resolve();
    }
  });
};

export const ClearSettingViewTransactionFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.SettingViewTransaction], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingViewTransaction)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
//#endregion

//#region SettingProductPrice
export const AddSettingProductPriceToIndexedDB = (key, settingProductPrice) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingPriceProduct], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingPriceProduct);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(JSON.stringify(settingProductPrice));
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, key);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddSettingProductPriceToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, key);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddSettingProductPriceToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request AddSettingViewTransactionToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetSettingProductPriceFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingPriceProduct], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingPriceProduct);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          const settingProductPrice = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(settingProductPrice);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request GetSettingProductPriceFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteSettingProductPriceFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingPriceProduct], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingPriceProduct);
      const request = objectStore.delete(key);

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };

      request.onerror = (err) => {
        console.error(`Error to request DeleteSettingProductPriceFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve();
    }
  });
};

export const ClearSettingPriceProductFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.SettingPriceProduct], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingPriceProduct)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
//#endregion

// #region Account
export const AddAccountToIndexedDB = (key, account) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.Account], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.Account);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(JSON.stringify(account));
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, key);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddAccountToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, key);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddAccountToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request AddAccountToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetAccountFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.Account], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.Account);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          const account = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(account);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request GetAccountFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteAccountFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.Account], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.Account);
      const request = objectStore.delete(key);

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };

      request.onerror = (err) => {
        console.error(`Error to request DeleteAccountFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve();
    }
  });
};

export const ClearAccountFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.Account], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.Account)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
// #endregion

// #region UserInApp
export const AddUserInAppToIndexedDB = (key, userInApp) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.UserInApp], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.UserInApp);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(JSON.stringify(userInApp));
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, key);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddUserInAppToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, key);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddUserInAppToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request AddUserInAppToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetUserInAppFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.UserInApp], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.UserInApp);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          const userInApp = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(userInApp);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request GetUserInAppFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteUserInAppFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.UserInApp], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.UserInApp);
      const request = objectStore.delete(key);

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };

      request.onerror = (err) => {
        console.error(`Error to request DeleteUserInAppFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve();
    }
  });
};

export const ClearUserInAppFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.UserInApp], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.UserInApp)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
// #endregion

// #region Package
export const AddPackageToIndexedDB = (key, _package) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.Package], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.Package);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(JSON.stringify(_package));
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, key);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddPackageToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, key);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddPackageToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request AddPackageToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetPackageFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.Package], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.Package);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          const packageData = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(packageData);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request GetPackageFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeletePackageFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.Package], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.Package);
      const request = objectStore.delete(key);

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };

      request.onerror = (err) => {
        console.error(`Error to request DeletePackageFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve();
    }
  });
};

export const ClearPackageFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.Package], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.Package)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
// #endregion

//#region GetSettingQuotationPartial
export const AddSettingQuotationPartialToIndexedDB = (key, settingQuotationPartial) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingQuotationPartial], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingQuotationPartial);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const encryptedSettingQuotationPartial = EncryptionIndexedDbCash(JSON.stringify(settingQuotationPartial));

        if (request.result) {
          const updateRequest = objectStore.put(encryptedSettingQuotationPartial, key);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddSettingQuotationPartialToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptedSettingQuotationPartial, key);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddSettingQuotationPartialToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request AddSettingQuotationPartialToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetSettingQuotationPartialFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingQuotationPartial], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingQuotationPartial);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          const settingQuotationPartial = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(settingQuotationPartial);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request GetSettingQuotationPartialFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteSettingQuotationPartialFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingQuotationPartial], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingQuotationPartial);
      const request = objectStore.delete(key);

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };

      request.onerror = (err) => {
        console.error(`Error to request DeleteSettingQuotationPartialFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve();
    }
  });
};

export const ClearSettingQuotationPartialFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.SettingQuotationPartial], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingQuotationPartial)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
//#endregion

//#region GetSettingPurchaseOrderPartial
export const AddSettingPurchaseOrderPartialToIndexedDB = (key, settingPurchaseOrderPartial) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingPurchaseOrderPartial], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingPurchaseOrderPartial);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const encryptedSettingPurchaseOrderPartial = EncryptionIndexedDbCash(
          JSON.stringify(settingPurchaseOrderPartial)
        );

        if (request.result) {
          const updateRequest = objectStore.put(encryptedSettingPurchaseOrderPartial, key);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddSettingPurchaseOrderPartialToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptedSettingPurchaseOrderPartial, key);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddSettingPurchaseOrderPartialToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request AddSettingPurchaseOrderPartialToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetSettingPurchaseOrderPartialFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingPurchaseOrderPartial], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingPurchaseOrderPartial);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          const settingPurchaseOrderPartial = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(settingPurchaseOrderPartial);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request GetSettingPurchaseOrderPartialFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteSettingPurchaseOrderPartialFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.SettingPurchaseOrderPartial], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingPurchaseOrderPartial);
      const request = objectStore.delete(key);

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };

      request.onerror = (err) => {
        console.error(`Error to request DeleteSettingPurchaseOrderPartialFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve();
    }
  });
};

export const ClearSettingPurchaseOrderPartialFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.SettingPurchaseOrderPartial], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.SettingPurchaseOrderPartial)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
//#endregion

// #region BankAccount
export const GetBankAccountFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.BankAccount], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.BankAccount);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          const bankAccount = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(bankAccount);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request GetBankAccountFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};

export const AddBankAccountToIndexedDB = (key, BankAccount) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.BankAccount], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.BankAccount);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(JSON.stringify(BankAccount));
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, key);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddBankAccountToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, key);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddBankAccountToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request AddBankAccountToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const DeleteBankAccountFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.BankAccount], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.BankAccount);
      const request = objectStore.delete(key);

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };

      request.onerror = (err) => {
        console.error(`Error to request DeleteBankAccountFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve();
    }
  });
};

export const ClearBankAccountFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.BankAccount], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.BankAccount)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
// #endregion

// #region Product
export const AddProductToIndexedDB = (key, product) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.Product], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.Product);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(JSON.stringify(product));
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, key);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddProductToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, key);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddAccountToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request AddAccountToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetProductFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.Product], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.Product);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          const account = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(account);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request GetAccountFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteProductFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.Product], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.Product);
      const request = objectStore.delete(key);

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };

      request.onerror = (err) => {
        console.error(`Error to request DeleteAccountFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve();
    }
  });
};

export const ClearProductFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.Product], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.Product)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
// #endregion

// #region ContactStatementRemarkFrequency
export const AddContactStatementRemarkFrequencyToIndexedDB = (key, contactStatementRemarkFrequency) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.ContactStatementRemarkFrequency], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.ContactStatementRemarkFrequency);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(JSON.stringify(contactStatementRemarkFrequency));
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, key);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddContactStatementRemarkFrequencyToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, key);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddContactStatementRemarkFrequencyToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request AddAccountToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetContactStatementRemarkFrequencyFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.ContactStatementRemarkFrequency], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.ContactStatementRemarkFrequency);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          const account = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(account);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request GetContactStatementRemarkFrequencyFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteContactStatementRemarkFrequencyFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.ContactStatementRemarkFrequency], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.ContactStatementRemarkFrequency);
      const request = objectStore.delete(key);

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };

      request.onerror = (err) => {
        console.error(`Error to request DeleteContactStatementRemarkFrequencyFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve();
    }
  });
};

export const ClearContactStatementRemarkFrequencyFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.ContactStatementRemarkFrequency], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.ContactStatementRemarkFrequency)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
// #endregion

// #region AssetTemplate
export const AddAssetTemplateToIndexedDB = (key, assetTemplate) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.AssetTemplate], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.AssetTemplate);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        const encryptDataCash = EncryptionIndexedDbCash(JSON.stringify(assetTemplate));
        if (request.result) {
          const updateRequest = objectStore.put(encryptDataCash, key);
          updateRequest.onsuccess = () => {
            db.close();
            resolve();
          };

          updateRequest.onerror = (err) => {
            console.error(`Error to updateRequest AddAssetTemplateToIndexedDB: ${err}`);
            db.close();
            resolve();
          };
        } else {
          const addRequest = objectStore.add(encryptDataCash, key);

          addRequest.onsuccess = (event) => {
            db.close();
            resolve();
          };

          addRequest.onerror = (err) => {
            if (err.target.error.name === "ConstraintError") {
              db.close();
              resolve();
            } else {
              console.error(`Error to addRequest AddAssetTemplateToIndexedDB: ${err}`);
              db.close();
              resolve();
            }
          };
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request AddAssetTemplateToIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(0);
    }
  });
};

export const GetAssetTemplateFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.AssetTemplate], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.AssetTemplate);
      const request = objectStore.get(key);

      request.onsuccess = (event) => {
        db.close();
        if (request.result) {
          const assetTemplateList = JSON.parse(DecryptionIndexedDbCash(request.result));
          resolve(assetTemplateList);
        } else {
          resolve(null);
        }
      };

      request.onerror = (err) => {
        console.error(`Error to request GetAssetTemplateFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve(null);
    }
  });
};

export const DeleteAssetTemplateFromIndexedDB = (key) => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const objectStore = db
        .transaction([ObjectStores.AssetTemplate], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.AssetTemplate);
      const request = objectStore.delete(key);

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };

      request.onerror = (err) => {
        console.error(`Error to request DeleteAssetTemplateFromIndexedDB: ${err}`);
        resolve(0);
      };
    } else {
      resolve();
    }
  });
};

export const ClearAssetTemplateFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    const db = await OpenCacheIndexedDB();
    if (db) {
      const request = db
        .transaction([ObjectStores.AssetTemplate], Enums.IDBTransactionMode.ReadWrite)
        .objectStore(ObjectStores.AssetTemplate)
        .clear();

      request.onerror = (event) => {
        db.close();
        resolve();
      };

      request.onsuccess = (event) => {
        db.close();
        resolve();
      };
    } else {
      resolve();
    }
  });
};
// #endregion

export const ClearAllCacheFromIndexedDB = () => {
  return new Promise(async (resolve, reject) => {
    await ClearAccessTokenFromIndexedDB();
    await ClearUserProfileFromIndexedDB();
    await ClearSelectedMerchantFromIndexedDB();
    await ClearSettingTableFromIndexedDB();
    await ClearSettingViewTransactionFromIndexedDB();
    await ClearSettingPriceProductFromIndexedDB();
    await ClearAccountFromIndexedDB();
    await ClearUserInAppFromIndexedDB();
    await ClearPackageFromIndexedDB();
    await ClearSettingQuotationPartialFromIndexedDB();
    await ClearSettingPurchaseOrderPartialFromIndexedDB();
    await ClearBankAccountFromIndexedDB();
    await ClearProductFromIndexedDB();
    await ClearContactStatementRemarkFrequencyFromIndexedDB();
    await ClearAssetTemplateFromIndexedDB();
    resolve(true);
  });
};
